import React, { createContext, useEffect, useState } from "react";

import api from "../../axios/axiosInstance";
import { useDispatch, useSelector } from "react-redux";
import { fetchProfile } from "../../data/redux/slices/ProfileSlice";
import { showToast } from "../../data/redux/slices/ToastSlice";
import { clearContactsCache } from "../../data/redux/slices/LeadSlice";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const SCOPES =
  "https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/calendar";

export const EmailAuthContext = createContext();

export const EmailAuthProvider = ({ children }) => {
  const [isGoogleSignedIn, setIsGoogleSignedIn] = useState(false);
  const [isMicrosoftSignedIn, setIsMicrosoftSignedIn] = useState(false);
  const userProfile = useSelector((state) => state.profile);
  const dispatch = useDispatch();

  const googleSignIn = async () => {
    try {
      const response = await api.post("connect/google");

      if (response.data.status === "success" && response.data.url) {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const messageHandler = (event) => {

          const data = event.data;
          if (data?.status === "success" && data.googleConnected) {
            dispatch(
              showToast({
                message: "Google account connected successfully",
                variant: "success",
              })
            );
            dispatch(fetchProfile());

            window.removeEventListener("message", messageHandler);
            popup?.close();
          }
        };
        window.addEventListener("message", messageHandler);

        const popup = window.open(
          response.data.url,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );
        if (!popup) {
          console.error("Popup blocked");
        }
      }
    } catch (error) {
      console.error("Google Sign-In initiation failed", error);
    }
  };

  const googleSignOut = async () => {
    try {
      const response = await api.post("disconnect/google");
      dispatch(fetchProfile());
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
    }
  };

  const microsoftSignIn = async () => {
    try {
      const response = await api.post("connect/microsoft");

      if (response.data.status === "success" && response.data.url) {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const messageHandler = (event) => {
          const data = event.data;
          if (data?.status === "success" && data.microsoftConnected) {
            dispatch(
              showToast({
                message: "Microsoft account connected successfully",
                variant: "success",
              })
            );
            dispatch(fetchProfile());
            window.removeEventListener("message", messageHandler);
            popup?.close();
          }
        };

        window.addEventListener("message", messageHandler);

        const popup = window.open(
          response.data.url,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );
      }
    } catch (error) {
      console.error("Microsoft Sign-In initiation failed", error);
    }
  };

  const microsoftSignOut = async () => {
    try {
      const response = await api.post("disconnect/microsoft");

      dispatch(fetchProfile());
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
    } catch (error) {
      dispatch(
        showToast({ message: error.response?.data?.message, variant: "danger" })
      );
    }
  };

  const zohoSignIn = async () => {
    try {
      const response = await api.post("api/zoho/connect", { dc: "com" });

      if (response.data.status === "success" && response.data.url) {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const messageHandler = (event) => {


          const data = event.data;
          if (data?.status === "success" && data.zohoConnected) {
            dispatch(
              showToast({
                message: "Zoho account connected successfully",
                variant: "success",
              })
            );
            dispatch(fetchProfile());

            window.removeEventListener("message", messageHandler);
            popup?.close();
          }
        };
        window.addEventListener("message", messageHandler);

        const popup = window.open(
          response.data.url,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );
        if (!popup) {
          console.error("Popup blocked");
        }
      }
    } catch (error) {
      console.error("Zoho Sign-In initiation failed", error);
    }
  };

  const zohoSignOut = async () => {
    try {
      const response = await api.post("api/zoho/disconnect");
      dispatch(fetchProfile());
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
    }
  };

  const metaSignIn = async () => {
    try {
      const response = await api.get("api/meta/connect");

      if (response.data.authUrl) {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        let popup = null;
        let messageReceived = false;

        const messageHandler = (event) => {

          const data = event.data;
          if (data?.status === "success") {
            messageReceived = true;
            dispatch(
              showToast({
                message: "Facebook connected successfully!",
                variant: "success",
              })
            );

            window.removeEventListener("message", messageHandler);
            if (popup && !popup.closed) {
              popup.close();
            }

            // Refresh profile to show connection status
            dispatch(fetchProfile());
          } else if (data?.status === "error") {
            messageReceived = true;
            dispatch(
              showToast({
                message: data.message || "Facebook connection failed",
                variant: "danger",
              })
            );
            window.removeEventListener("message", messageHandler);
            if (popup && !popup.closed) {
              popup.close();
            }
          }
        };

        window.addEventListener("message", messageHandler);

        popup = window.open(
          response.data.authUrl,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );

        if (!popup) {
          console.error("[Meta] Popup blocked");
          dispatch(
            showToast({
              message: "Popup blocked. Please allow popups for this site.",
              variant: "danger",
            })
          );
          window.removeEventListener("message", messageHandler);
          return;
        }

        // Fallback: Check if popup closed without message (OAuth might have succeeded)
        const checkInterval = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(checkInterval);

            if (!messageReceived) {

              // Wait a moment then check if connection succeeded
              setTimeout(() => {
                dispatch(fetchProfile()).then(() => {
                  // Profile will be updated, UI will reflect connection status
                });
              }, 500);

              window.removeEventListener("message", messageHandler);
            }
          }
        }, 500);

        // Cleanup after 5 minutes
        setTimeout(() => {
          clearInterval(checkInterval);
          window.removeEventListener("message", messageHandler);
        }, 300000);
      }
    } catch (error) {
      console.error("[Meta] Sign-in initiation failed", error);
      dispatch(
        showToast({
          message: "Failed to initiate Facebook connection",
          variant: "danger",
        })
      );
    }
  };

  const fetchMetaPages = async () => {
    try {
      const response = await api.get("api/meta/pages");
      return response.data.leadForms || [];
    } catch (error) {
      console.error("[Meta] Failed to fetch lead forms", error);
      dispatch(
        showToast({
          message: "Failed to fetch Facebook lead forms",
          variant: "danger",
        })
      );
      return [];
    }
  };

  const selectMetaPage = async (
    formId,
    pageId,
    pageAccessToken,
    agentId = null
  ) => {
    try {
      dispatch(
        showToast({
          message: "Importing leads from selected form...",
          variant: "info",
        })
      );

      // Import leads directly from selected form
      const importRes = await api.post("api/meta/import-leads", {
        formId,
        pageId,
        pageAccessToken,
        agentId,
      });

      dispatch(fetchProfile());
      dispatch(
        showToast({
          message:
            importRes.data.message ||
            `Successfully imported ${importRes.data.imported} leads from Facebook`,
          variant: "success",
        })
      );
      dispatch(clearContactsCache());
    } catch (error) {
      console.error("[Meta] Failed to import leads", error);
      dispatch(
        showToast({
          message:
            error.response?.data?.message || "Failed to import Meta leads",
          variant: "error",
        })
      );
    }
  };

  const subscribeToMetaPage = async (
    pageId,
    pageName,
    pageAccessToken,
    subscribe
  ) => {
    try {
      dispatch(
        showToast({
          message: subscribe
            ? "Subscribing to page..."
            : "Unsubscribing from page...",
          variant: "info",
        })
      );

      const res = await api.post("api/meta/pages/subscribe", {
        pageId,
        pageName,
        pageAccessToken,
        subscribe,
      });

      dispatch(fetchProfile());
      dispatch(
        showToast({
          message: res.data.message,
          variant: "success",
        })
      );
    } catch (error) {
      console.error("[Meta] Failed to manage subscription", error);
      dispatch(
        showToast({
          message: error.response?.data?.message || "Failed to import leads",
          variant: "danger",
        })
      );
    }
  };

  const metaSignOut = async () => {
    try {
      const response = await api.post("disconnect/meta");
      dispatch(fetchProfile());
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
    } catch (error) {
      dispatch(
        showToast({
          message:
            error.response?.data?.message || "Failed to disconnect Facebook",
          variant: "danger",
        })
      );
    }
  };

  const smtpSignIn = async (smtpData) => {
    try {

      const response = await api.post("connect/smtp", smtpData);

      dispatch(fetchProfile());
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
    } catch (error) {
      dispatch(
        showToast({ message: error.response.data.message, variant: "danger" })
      );
    }
  };

  const smtpSignOut = async () => {
    try {
      const response = await api.post("disconnect/smtp");

      dispatch(fetchProfile());
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
    } catch (error) {
      dispatch(
        showToast({ message: error.response?.data?.message, variant: "danger" })
      );
    }
  };

  const zoomSignIn = async () => {
    try {
      const response = await api.post("connect/zoom");

      if (response.data.status === "success" && response.data.url) {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const messageHandler = (event) => {
          const data = event.data;
          if (data?.status === "success" && data.zoomConnected) {
            dispatch(
              showToast({
                message: "Zoom account connected successfully",
                variant: "success",
              })
            );
            dispatch(fetchProfile());

            window.removeEventListener("message", messageHandler);
            popup?.close();
          }
        };
        window.addEventListener("message", messageHandler);

        const popup = window.open(
          response.data.url,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );
        if (!popup) {
          console.error("Popup blocked");
        }
      }
    } catch (error) {
      console.error("Zoom Sign-In initiation failed", error);
    }
  };

  const zoomSignOut = async () => {
    try {
      const response = await api.post("disconnect/zoom");
      dispatch(fetchProfile());
      dispatch(
        showToast({ message: response.data.message, variant: "success" })
      );
    } catch (error) {
      dispatch(
        showToast({ message: error.response?.data?.message, variant: "danger" })
      );
    }
  };
  // Add to EmailAuthContext.jsx

  const hubspotSignIn = async () => {
    try {
      const response = await api.post("api/hubspot/connect");

      if (response.data.status === "success" && response.data.url) {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const messageHandler = (event) => {
          const data = event.data;
          if (data?.status === "success" && data.hubspotConnected) {
            dispatch(
              showToast({
                message: "HubSpot account connected successfully",
                variant: "success",
              })
            );
            dispatch(fetchProfile());
            window.removeEventListener("message", messageHandler);
            popup?.close();
          }
        };
        window.addEventListener("message", messageHandler);

        const popup = window.open(
          response.data.url,
          "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );
        if (!popup) {
          console.error("Popup blocked");
        }
      }
    } catch (error) {
      console.error("HubSpot Sign-In initiation failed", error);
    }
  };

  const hubspotSignOut = async () => {
    try {
      const response = await api.post("api/hubspot/disconnect");
      dispatch(fetchProfile());
      dispatch(showToast({ message: response.data.message, variant: "success" }));
    } catch (error) {
      dispatch(
        showToast({ message: error.response?.data?.message, variant: "danger" })
      );
    }
  };
  const pipedriveSignIn = async () => {
    try {
      const response = await api.post("api/pipedrive/connect");

      if (response.data.status === "success" && response.data.url) {
        const width = 500, height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const messageHandler = (event) => {
          const data = event.data;
          if (data?.status === "success" && data.pipedriveConnected) {
            dispatch(showToast({ message: "Pipedrive connected successfully", variant: "success" }));
            dispatch(fetchProfile());
            window.removeEventListener("message", messageHandler);
            popup?.close();
          }
        };
        window.addEventListener("message", messageHandler);

        const popup = window.open(
          response.data.url, "_blank",
          `width=${width},height=${height},left=${left},top=${top}`
        );
        if (!popup) console.error("Popup blocked");
      }
    } catch (error) {
      console.error("Pipedrive Sign-In failed", error);
    }
  };

  const pipedriveSignOut = async () => {
    try {
      const response = await api.post("api/pipedrive/disconnect");
      dispatch(fetchProfile());
      dispatch(showToast({ message: response.data.message, variant: "success" }));
    } catch (error) {
      dispatch(showToast({ message: error.response?.data?.message, variant: "danger" }));
    }
  };



  return (
    <EmailAuthContext.Provider
      value={{
        isGoogleSignedIn,
        googleSignIn,
        googleSignOut,
        isMicrosoftSignedIn,
        microsoftSignIn,
        microsoftSignOut,
        smtpSignIn,
        smtpSignOut,
        zohoSignIn,
        zohoSignOut,
        metaSignIn,
        metaSignOut,
        zoomSignIn,
        zoomSignOut,
        fetchMetaPages,
        hubspotSignIn,
        hubspotSignOut,
        pipedriveSignIn,
        pipedriveSignOut,
        selectMetaPage,
        subscribeToMetaPage,
      }}
    >
      {children}
    </EmailAuthContext.Provider>
  );
};
